import Papa from "papaparse";

/**
 * Client-side parsing for contact imports.
 *
 * CSV files and pasted spreadsheet data are parsed here with PapaParse;
 * Google Sheets are fetched + parsed by the backend (CORS). Either way we end
 * up with { columns, rows } and hand it to the column-mapping step.
 */

export const OUR_FIELDS = [
  { key: "name", label: "Name" },
  { key: "phone_number", label: "Phone number", required: true },
  { key: "country_code", label: "Country code" },
  { key: "email", label: "Email" },
  { key: "wa_id", label: "WhatsApp ID" },
  { key: "status", label: "Status" },
  { key: "notes", label: "Notes" },
];

const MAX_ROWS = 5000;

const toResult = (parsed) => {
  const fields = (parsed.meta?.fields || [])
    .map((f) => String(f || "").trim())
    .filter(Boolean);

  // de-dupe blank / repeated headers
  const seen = new Set();
  const columns = fields.map((f, i) => {
    let name = f || `column_${i + 1}`;
    let n = 2;
    while (seen.has(name)) name = `${f} (${n++})`;
    seen.add(name);
    return name;
  });

  const allRows = Array.isArray(parsed.data) ? parsed.data : [];
  const rows = allRows
    .map((row) => {
      const clean = {};
      columns.forEach((col, i) => {
        const original = fields[i];
        const value = row[original];
        clean[col] = value == null ? "" : String(value).trim();
      });
      return clean;
    })
    .filter((row) => Object.values(row).some((v) => v !== ""));

  return {
    columns,
    rows: rows.slice(0, MAX_ROWS),
    totalRows: rows.length,
    truncated: rows.length > MAX_ROWS,
  };
};

export function parseCsvFile(file) {
  return new Promise((resolve, reject) => {
    if (!(file instanceof File)) {
      reject(new Error("Please choose a file."));
      return;
    }
    Papa.parse(file, {
      header: true,
      skipEmptyLines: "greedy",
      transformHeader: (h) => String(h || "").trim(),
      complete: (res) => {
        const result = toResult(res);
        if (!result.columns.length) {
          reject(new Error("Couldn't find any columns in that file. Is the first row a header?"));
          return;
        }
        if (!result.rows.length) {
          reject(new Error("That file has headers but no data rows."));
          return;
        }
        resolve(result);
      },
      error: (err) => reject(new Error(err?.message || "Couldn't read that file.")),
    });
  });
}

export function parseDelimitedText(text) {
  const trimmed = String(text || "").trim();
  if (!trimmed) throw new Error("Paste some rows first.");

  const res = Papa.parse(trimmed, {
    header: true,
    skipEmptyLines: "greedy",
    transformHeader: (h) => String(h || "").trim(),
  });

  const result = toResult(res);
  if (!result.columns.length) {
    throw new Error("Couldn't read that. Include a header row, then the data.");
  }
  if (!result.rows.length) {
    throw new Error("Add at least one data row under the header.");
  }
  return result;
}

const ALIASES = {
  name: ["name", "full name", "fullname", "contact name", "customer name", "first name"],
  phone_number: ["phone", "phone number", "phonenumber", "mobile", "mobile number", "whatsapp", "whatsapp number", "contact", "number", "msisdn", "cell"],
  country_code: ["country code", "countrycode", "dial code", "cc"],
  email: ["email", "e-mail", "email address", "mail"],
  wa_id: ["wa id", "wa_id", "whatsapp id", "waid"],
  status: ["status"],
  notes: ["notes", "note", "remark", "remarks", "comment", "comments"],
};

/** Best-guess { field: column } from column names. */
export function suggestMapping(columns = []) {
  const mapping = {};
  const used = new Set();

  Object.entries(ALIASES).forEach(([field, names]) => {
    const hit = columns.find((col) => {
      if (used.has(col)) return false;
      const norm = col.toLowerCase().trim();
      return names.includes(norm) || (field === "phone_number" && norm.includes("phone"));
    });
    if (hit) {
      mapping[field] = hit;
      used.add(hit);
    }
  });

  return mapping;
}
